import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { type CountryCode } from 'libphonenumber-js';
import { Subscription, filter, finalize, take } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmActionDialogComponent,
  type ConfirmActionDialogData,
} from '../pages/manage-client/confirm-action-dialog.component';
import { IconModule } from '../shared/icon/icon.module';
import { SharedModule } from '../shared/shared.module';
import { UserAccountApiService, type AdminProfileData } from '../core/services/user-account-api.service';
import { AdminSessionProfileService, DEFAULT_ADMIN_AVATAR } from '../core/services/admin-session-profile.service';
import { IntlPhoneFieldComponent } from '../shared/components/intl-phone-field/intl-phone-field.component';
import {
  dialStringForCountry,
  normalizeNationalDigits,
  splitPhoneForForm,
} from '../shared/utils/international-phone';

@Component({
  templateUrl: './user-account-settings.html',
  standalone: true,
  imports: [CommonModule, FormsModule, IconModule, RouterModule, SharedModule, IntlPhoneFieldComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class UserAccountSettingsComponent implements OnInit, OnDestroy {
  @ViewChild(IntlPhoneFieldComponent) phoneField?: IntlPhoneFieldComponent;

  activeTab = 'home';

  currentPassword = '';
  newPassword = '';
  confirmPassword = '';

  passwordSubmitting = false;
  passwordError = '';
  passwordSuccess = '';

  fullName = '';
  emailId = '';
  phoneCountryIso: CountryCode = 'IN';
  phoneNational = '';

  profileErrors: Record<string, string> = {};
  profileGeneralError = '';
  profileSuccess = '';
  profileSubmitting = false;

  pictureUploading = false;
  pictureDeleting = false;
  pictureError = '';
  pictureSuccess = '';

  avatarUrl = DEFAULT_ADMIN_AVATAR;

  /** True when a custom image is stored (not only the default placeholder). */
  get hasStoredProfilePicture(): boolean {
    const pp = this.adminProfile.getLastProfile()?.profile_picture;
    if (!pp || typeof pp !== 'object' || Array.isArray(pp)) {
      return false;
    }
    const path = (pp as Record<string, unknown>)['storage_path'];
    return typeof path === 'string' && path.trim().length > 0;
  }

  private snapSub?: Subscription;

  constructor(
    private readonly userAccountApi: UserAccountApiService,
    private readonly router: Router,
    private readonly adminProfile: AdminSessionProfileService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.snapSub = this.adminProfile.snapshot$.subscribe((s) => {
      this.avatarUrl = s.avatarUrl;
    });

    const cached = this.adminProfile.getLastProfile();
    if (cached) {
      this.patchFormFromProfile(cached);
      return;
    }

    this.userAccountApi
      .getAdminProfile()
      .pipe(take(1))
      .subscribe({
        next: (env) => {
          if (env?.status && env.data) {
            this.adminProfile.applyFromProfileData(env.data);
            this.patchFormFromProfile(env.data);
          }
        },
        error: () => {
          /* guard normally prevents this */
        },
      });
  }

  ngOnDestroy(): void {
    this.snapSub?.unsubscribe();
  }

  /** Undo edits and restore fields from last saved profile. */
  resetProfileDetails(): void {
    if (this.profileSubmitting) {
      return;
    }
    const lp = this.adminProfile.getLastProfile();
    if (lp) {
      this.patchFormFromProfile(lp);
    }
    this.profileErrors = {};
    this.profileGeneralError = '';
    this.profileSuccess = '';
  }

  /** Clear password fields and inline messages. */
  resetPasswordFields(): void {
    if (this.passwordSubmitting) {
      return;
    }
    this.currentPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
    this.passwordError = '';
    this.passwordSuccess = '';
  }

  clearProfileFieldError(field: string): void {
    const next = { ...this.profileErrors };
    delete next[field];
    this.profileErrors = next;
    this.profileGeneralError = '';
  }

  submitProfile(ev: Event): void {
    ev.preventDefault();
    this.profileGeneralError = '';
    this.profileSuccess = '';
    this.profileErrors = {};

    const name = this.fullName.trim();
    if (!name) {
      this.profileErrors['fullName'] = 'Full name is required.';
    }

    const email = this.emailId.trim().toLowerCase();
    if (!email) {
      this.profileErrors['emailId'] = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.profileErrors['emailId'] = 'Please enter a valid email address.';
    }

    this.phoneField?.refreshError();
    const nationalDigits = normalizeNationalDigits(this.phoneNational);
    if (nationalDigits.length > 0) {
      if (this.phoneField?.fieldError) {
        this.profileErrors['mobile'] = this.phoneField.fieldError;
      } else if (nationalDigits.length !== 10) {
        this.profileErrors['mobile'] = 'Mobile number must be exactly 10 digits.';
      }
    }

    if (Object.keys(this.profileErrors).length > 0) {
      return;
    }

    const body: {
      full_name: string;
      email_id: string;
      country_code?: string;
      mobile_number?: string;
    } = {
      full_name: name,
      email_id: email,
    };

    if (nationalDigits.length === 10) {
      body.country_code = dialStringForCountry(this.phoneCountryIso);
      body.mobile_number = nationalDigits;
    }

    this.profileSubmitting = true;
    this.userAccountApi
      .updateAdminProfile(body)
      .pipe(finalize(() => (this.profileSubmitting = false)))
      .subscribe({
        next: (res) => {
          if (!res?.status) {
            this.profileGeneralError = res?.message || 'Could not save profile.';
            return;
          }
          if (res.data) {
            this.adminProfile.applyFromProfileData(res.data);
            this.patchFormFromProfile(res.data);
          }
          this.profileSuccess = res.message || 'Profile saved.';
          setTimeout(() => {
            this.profileSuccess = '';
          }, 4000);
        },
        error: (err: unknown) => {
          this.profileGeneralError = this.parseProfileApiError(err);
        },
      });
  }

  onProfilePictureSelected(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }

    this.pictureError = '';
    this.pictureSuccess = '';

    if (!file.type.startsWith('image/')) {
      this.pictureError = 'Please choose an image file (PNG, JPEG, or WebP).';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.pictureError = 'Image must be 5 MB or smaller.';
      return;
    }

    this.pictureUploading = true;
    this.userAccountApi
      .updateAdminProfilePicture(file)
      .pipe(finalize(() => (this.pictureUploading = false)))
      .subscribe({
        next: (res) => {
          if (!res?.status) {
            this.pictureError = res?.message || 'Upload failed.';
            return;
          }
          if (res.data) {
            this.adminProfile.applyFromProfileData(res.data);
          }
          this.pictureSuccess = res.message || 'Profile photo updated.';
          setTimeout(() => {
            this.pictureSuccess = '';
          }, 4000);
        },
        error: (err: unknown) => {
          this.pictureError = this.adminProfile.parseHttpError(err, 'Could not upload profile photo.');
        },
      });
  }

  removeProfilePicture(): void {
    if (!this.hasStoredProfilePicture || this.pictureDeleting || this.pictureUploading) {
      return;
    }
    const userId = this.adminProfile.getLastProfile()?.id;
    if (userId == null) {
      this.pictureError = 'Could not determine your account. Refresh the page and try again.';
      return;
    }

    const data: ConfirmActionDialogData = {
      title: 'Remove profile photo?',
      message:
        'Your profile photo will be removed from your account. The default avatar will appear in the header and on this page.',
      confirmLabel: 'Remove',
      confirmColor: 'warn',
    };

    const ref = this.dialog.open(ConfirmActionDialogComponent, {
      width: '440px',
      disableClose: true,
      data,
    });

    ref
      .afterClosed()
      .pipe(filter((ok) => ok === true))
      .subscribe(() => this.runRemoveProfilePicture(userId));
  }

  private runRemoveProfilePicture(userId: number): void {
    this.pictureError = '';
    this.pictureSuccess = '';
    this.pictureDeleting = true;
    this.userAccountApi
      .deleteUserProfilePicture(userId)
      .pipe(finalize(() => (this.pictureDeleting = false)))
      .subscribe({
        next: (res) => {
          if (!res?.status) {
            this.pictureError = res?.message || 'Could not remove photo.';
            return;
          }
          if (res.data) {
            this.adminProfile.applyFromProfileData(res.data);
          }
          this.pictureSuccess = res.message || 'Profile photo removed.';
          setTimeout(() => {
            this.pictureSuccess = '';
          }, 4000);
        },
        error: (err: unknown) => {
          this.pictureError = this.adminProfile.parseHttpError(err, 'Could not remove profile photo.');
        },
      });
  }

  private patchFormFromProfile(data: AdminProfileData): void {
    this.fullName = data.full_name ?? '';
    this.emailId = data.email_id ?? '';
    const split = splitPhoneForForm(data.country_code, data.mobile_number, 'IN');
    this.phoneCountryIso = split.iso;
    this.phoneNational = split.national;
  }

  private parseProfileApiError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (body && typeof body === 'object') {
        const msg = (body as { message?: unknown }).message;
        if (typeof msg === 'string') {
          return msg;
        }
        if (Array.isArray(msg)) {
          return msg.map(String).join(' ');
        }
      }
      if (err.status === 401) {
        return 'Your session expired. Please sign in again.';
      }
      if (err.status === 0) {
        return 'Cannot reach the API. Check that the backend is running.';
      }
    }
    return 'Could not save profile. Please try again.';
  }

  submitChangePassword(ev: Event): void {
    ev.preventDefault();
    this.passwordError = '';
    this.passwordSuccess = '';

    if (!this.currentPassword || !this.newPassword || !this.confirmPassword) {
      this.passwordError = 'Please fill in all password fields.';
      return;
    }
    if (this.newPassword !== this.confirmPassword) {
      this.passwordError = 'New password and confirmation do not match.';
      return;
    }
    if (this.newPassword === this.currentPassword) {
      this.passwordError = 'New password must be different from your current password.';
      return;
    }

    this.passwordSubmitting = true;
    this.userAccountApi
      .changePassword({
        old_password: this.currentPassword,
        password: this.newPassword,
        confirm_password: this.confirmPassword,
      })
      .pipe(finalize(() => (this.passwordSubmitting = false)))
      .subscribe({
        next: (res) => {
          if (!res?.status) {
            this.passwordError = res?.message || 'Could not update password.';
            return;
          }
          this.passwordSuccess =
            res.message ||
            'Password updated. Please sign in again — your other sessions were signed out for security.';
          this.currentPassword = '';
          this.newPassword = '';
          this.confirmPassword = '';
          setTimeout(() => {
            void this.router.navigate(['/auth/signin']);
          }, 2000);
        },
        error: (err: unknown) => {
          this.passwordError = this.parseChangePasswordError(err);
        },
      });
  }

  private parseChangePasswordError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (body && typeof body === 'object' && 'message' in body) {
        const m = (body as { message: unknown }).message;
        if (typeof m === 'string') {
          return m;
        }
        if (Array.isArray(m)) {
          return m.map(String).join(' ');
        }
      }
      if (err.status === 401) {
        return 'Your session expired. Please sign in again.';
      }
      if (err.status === 0) {
        return 'Cannot reach the API. Check that the backend is running.';
      }
    }
    return 'Could not update password. Please try again.';
  }
}
